import { useState } from "react";
import dayjs from "dayjs";

import { cancelScheduleAppointmentsApi } from "@/utils/apis/doctor/appointment";
import { IAPIStatusCode } from "@/utils/apis/APIEndpointConfig";

import { useAppDispatch, useAppSelector } from "@/store/hooks";
import {
  fetchScheduleAppointmentList,
  fetchSeachedAppointmentList,
  resetScheduleSelection,
} from "@/store/slices/doctor/schedule";
import { setAppointmentDetail } from "@/store/slices/doctor/appointment";
import { setVideoAppointmentDetail } from "@/store/slices/videoAppointment";
import { universalSearchFilterDetails } from "@/store/slices/doctor/universalSearchFilter";

import {
  IAppointmentPermissionEnums,
  IModalKeyEnums,
} from "@/models/permission";

import { INotifyEnum, notify } from "@/utils/toaster";
import { isClinic } from "@/utils";
import { usePermissionRestriction } from "./usePermissionRestriction";

const useCancelScheduleAppointments = () => {
  const [isCancelAppointmentLoading, setCancelAppointmentLoading] =
    useState<boolean>(false);
  const isClinical = isClinic();
  const { selectedSchedules, selectedAppointments } = useAppSelector(
    (state) => state.schedule.selection
  );
  const { searchKeyword } = useAppSelector(
    (state) => state.pharmacyClinicDiagnostic
  );
  const { appointmentDetail } = useAppSelector((state) => state.appointment);
  const { schedule } = useAppSelector(universalSearchFilterDetails);

  const dispatch = useAppDispatch();

  const permissionRestrictionHandler = usePermissionRestriction();

  const cancelScheduleAppointments = async () => {
    const isCancelAppointmentPermissionGranted = permissionRestrictionHandler(
      IModalKeyEnums.APPOINTMENT,
      IAppointmentPermissionEnums.CANCEL_APPOINTMENT
    );
    if (!isCancelAppointmentPermissionGranted) return;

    setCancelAppointmentLoading(true);
    const cancelScheduleAppointmentsResponse =
      await cancelScheduleAppointmentsApi({
        schedules: selectedSchedules,
        appointments:
          appointmentDetail?.id?.length > 0
            ? [appointmentDetail?.id]
            : selectedAppointments,
      });
    if (
      cancelScheduleAppointmentsResponse.statusCode === IAPIStatusCode.SUCCESS
    ) {
      dispatch(
        fetchScheduleAppointmentList({
          ...(schedule?.startDate && schedule?.endDate
            ? {
                start: schedule.startDate,
                end: dayjs(schedule.endDate).add(1, "day").format("YYYY-MM-DD"),
              }
            : {}),
        })
      );

      if (isClinical && searchKeyword) {
        dispatch(fetchSeachedAppointmentList({ search: searchKeyword }));
      }
      dispatch(resetScheduleSelection());
      dispatch(setAppointmentDetail({}));
      dispatch(setVideoAppointmentDetail({}));
      notify(INotifyEnum.SUCCESS, cancelScheduleAppointmentsResponse.message);
      setCancelAppointmentLoading(false);
    } else {
      setCancelAppointmentLoading(false);
    }
  };

  return { cancelScheduleAppointments, isCancelAppointmentLoading };
};

export default useCancelScheduleAppointments;
